import {useInfiniteQuery} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient'
import {httpGetMailerRecipients} from '@/features/mailer/http/mailer.http'
import {MailerRecipientGroup, MailerRecipients} from '@/features/mailer/types'
import {MAILER_RECIPIENTS_LIST_PAGE_SIZE} from '@/features/mailer/constants'
import {infiniteQueryFetchNextPage} from '@/utilities/helpers'

interface UseMailerRecipientsListParams {
    recipients: MailerRecipients | null
    group: MailerRecipientGroup
    enabled: boolean
}

/**
 * Paginated recipients browser per group (OPSSTD-3649): powers the scrollable
 * preview list, separate from the lightweight /recipients/preview counter.
 */
export const useMailerRecipientsList = ({recipients, group, enabled}: UseMailerRecipientsListParams) => {
    const query = useInfiniteQuery({
        queryKey: [QUERY_KEYS.MAILER_RECIPIENTS_LIST, group, recipients],
        queryFn: ({pageParam}) =>
            httpGetMailerRecipients({
                recipients: recipients as MailerRecipients,
                group,
                page: pageParam,
                limit: MAILER_RECIPIENTS_LIST_PAGE_SIZE
            }),
        initialPageParam: 1,
        getNextPageParam: lastPage =>
            lastPage.meta.currentPage < lastPage.meta.lastPage ? lastPage.meta.currentPage + 1 : null,
        enabled: enabled && recipients !== null
    })

    return {
        ...query,
        fetchNextPage: () => infiniteQueryFetchNextPage(query),
        recipients: query.data?.pages ? query.data.pages.flatMap(page => page.data) : []
    }
}
